import React, { useState, useEffect, useRef } from 'react';
import { FaInstagram } from 'react-icons/fa';
import { getInstagramPosts } from '../services/api';

const InstagramFeed = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const embedRefs = useRef([]);

    useEffect(() => {
        fetchInstagramPosts();
    }, []);

    // Load Instagram embed scripts and process embeds
    useEffect(() => {
        if (posts.length > 0 && posts.some(p => p.embedHtml)) {
            // Check if script already exists
            let script = document.querySelector('script[src="https://www.instagram.com/embed.js"]');
            
            if (!script) {
                // Instagram embed script
                script = document.createElement('script');
                script.src = 'https://www.instagram.com/embed.js';
                script.async = true;
                script.defer = true;
                document.body.appendChild(script);
                console.log('Instagram embed.js script loaded');
            }

            // Process embeds after script loads
            const processEmbeds = () => {
                if (window.instgrm && window.instgrm.Embeds) {
                    console.log('Processing Instagram embeds...');
                    window.instgrm.Embeds.process();
                } else {
                    // Retry after a short delay if script not loaded yet
                    setTimeout(processEmbeds, 200);
                }
            };

            // Wait a bit for script to load, then process
            setTimeout(() => {
                if (window.instgrm && window.instgrm.Embeds) {
                    processEmbeds();
                } else {
                    script.onload = processEmbeds;
                    // Also try processing after a delay
                    setTimeout(processEmbeds, 1000);
                }
            }, 500);
        }
    }, [posts]);

    const fetchInstagramPosts = async () => {
        try {
            setLoading(true);
            setError(null);
            // Use the API service function which handles URL construction correctly
            // Set a timeout to avoid long waits if server is not warmed up
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Request timeout')), 5000)
            );
            
            const data = await Promise.race([
                getInstagramPosts(),
                timeoutPromise
            ]);
            
            console.log('Instagram posts response:', data);
            
            if (data.status === 'success') {
                const postsData = data.data || [];
                console.log('Posts data:', postsData);
                setPosts(postsData);
                // Don't set error if no posts - just show placeholder
            }
        } catch (err) {
            console.error('Error fetching Instagram posts:', err);
            // Don't set error - just show placeholder
            setPosts([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter posts that have embed HTML
    const postsWithEmbeds = posts.filter(p => p.embedHtml);
    const hasEmbeds = postsWithEmbeds.length > 0;

    return (
        <div className="bg-gradient-to-b from-white to-gray-50 py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-3 mb-4">
                        <FaInstagram className="text-4xl md:text-5xl text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600" />
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                            Follow Us on Instagram
                        </h2>
                    </div>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-6">
                        {hasEmbeds 
                            ? 'Check out our latest posts, videos, and exclusive deals'
                            : 'Stay updated with our latest products, exclusive deals, and behind-the-scenes content'
                        }
                    </p>
                    <a
                        href="https://www.instagram.com/supersaverz/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                    >
                        <FaInstagram className="text-xl" />
                        <span>@supersaverz</span>
                    </a>
                </div>

                {/* Content Section */}
                {loading ? (
                    // Loading state
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                        <p className="mt-4 text-gray-600">Loading Instagram feed...</p>
                    </div>
                ) : hasEmbeds ? (
                    // Show embeds when available
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {postsWithEmbeds.map((post, index) => (
                            <div
                                key={post.id || index}
                                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                            >
                                {/* Instagram Embed */}
                                <div 
                                    className="instagram-embed-container"
                                    dangerouslySetInnerHTML={{ __html: post.embedHtml }}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    // Placeholder when no embeds available
                    <div className="text-center py-12">
                        <div className="max-w-md mx-auto">
                            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 mb-6">
                                <FaInstagram className="text-5xl text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                Stay Connected
                            </h3>
                            <p className="text-gray-600 mb-6">
                                Follow us on Instagram to see our latest posts, exclusive deals, and behind-the-scenes content
                            </p>
                            <a
                                href="https://www.instagram.com/supersaverz/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-full hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105"
                            >
                                <FaInstagram className="text-xl" />
                                <span>Follow @supersaverz</span>
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InstagramFeed;

